import { FieldPath } from "react-hook-form";
import { useStore } from "src/store/store";

import {
	Table,
	TableHeader,
	TableColumn,
	TableBody,
	TableRow,
	TableCell,
	Button,
	Chip,
	ChipProps,
} from "@heroui/react";
import { useAppApi } from "src/appApi";
import { useTranslation } from "react-i18next";
import { TDoctor, TBroker, serviceLevelLabels, serviceLevelOf } from "@shared/entities";
import { get } from "lodash";
import { TRequest } from "@shared/entities";
import { Icon } from "@iconify/react";
import { useConfirm } from "src/hooks/useConfirm";

export function RequestsTable({
	requests = [],
	doctors = [],
	brokers = [],
	onSave,
	onView,
}: {
	requests: TRequest[];
	doctors: TDoctor[];
	brokers?: TBroker[];
	onSave?: () => void;
	/** For a client: opens one of their own requests. Admin, doctor and broker
	 *  rows keep their own actions and ignore it. */
	onView?: (request: TRequest) => void;
}) {
	const { t } = useTranslation(["admin", "common"]);
	const { user } = useStore();

	// Who filed it, the doctor and the broker are admin-only columns: a doctor
	// or broker does not need them, and a client looking at their own history
	// must not see staff ids.
	const isAdminView = !!user?.admin;
	// A patient looking at their own history: their own name and ID on every row
	// say nothing, and dropping them lets the table fit a phone screen.
	const isClientView = !user?.admin && !user?.recommendingDoctor && !user?.broker;
	const ownDetailsKeys: string[] = ["clientDetails.IDNumber", "clientDetails.firstName", "clientDetails.lastName"];

	const baseColumns = [
		{
			key: "requestType" as const,
			label: "סוג",
		},
		{
			key: "createdAt" as const,
			label: "תאריך",
		},
		{
			key: "clientDetails.IDNumber" as const,
			label: "תעודת זהות",
		},
		{
			key: "clientDetails.firstName" as const,
			label: "שם פרטי",
		},
		{
			key: "clientDetails.lastName" as const,
			label: "שם משפחה",
		},
		{
			key: "status" as const,
			label: "סטטוס",
		},
	];

	const doctorColumn = {
		key: "doctor.id" as const,
		label: "רופא",
	};

	const brokerColumn = {
		key: "brokerId" as const,
		label: "מתווך",
	};

	const actionsColumn = {
		key: "actions" as const,
		label: "פעולות",
	};

	/** Who filed the request: the clinic (admin / broker) or the patient on their
	 *  own. First, because it decides how the admin treats the request. */
	const createdByColumn = {
		key: "createdBy" as const,
		label: "הוגש ע״י",
	};

	const columns: Array<{ key: string; label: string }> = [
		...(isAdminView ? [createdByColumn] : []),
		...baseColumns.filter((c) => !isClientView || !ownDetailsKeys.includes(c.key)),
		// Doctor and broker columns: admin view only
		...(isAdminView ? [doctorColumn] : []),
		...(isAdminView ? [brokerColumn] : []),
		// Show actions column for all views (including broker)
		actionsColumn,
	];

	const renderCell = (item: TRequest, key: FieldPath<TRequest>) => {
		const value = get(item, key);

		if (key === "requestType") {
			const color: Record<TRequest["requestType"], ChipProps["color"]> = {
				increase: "warning",
				new: "primary",
				renew: "secondary",
			};
			return (
				<div className="flex flex-col gap-1">
					<Chip color={color[value as TRequest["requestType"]]} variant="solid" radius="sm">
						{t(`common:requestTypes.${value as TRequest["requestType"]}`)}
					</Chip>
					{item.isRecommendationOnly && (
						<Chip color="warning" variant="flat" radius="sm" size="sm">
							המלצה בלבד
						</Chip>
					)}
					{isAdminView && (
						<Chip
							color={serviceLevelOf(item) === "full" ? "primary" : "default"}
							variant="flat"
							radius="sm"
							size="sm"
						>
							{serviceLevelLabels[serviceLevelOf(item)]}
						</Chip>
					)}
				</div>
			);
		}

		if (key === "createdAt") {
			return new Date(value as any).toLocaleDateString();
		}

		if (key === "createdBy") {
			const meta: Record<TRequest["createdBy"], { label: string; color: ChipProps["color"]; icon: string }> = {
				admin: { label: "אדמין", color: "secondary", icon: "lucide:shield" },
				broker: { label: "מתווך", color: "warning", icon: "lucide:briefcase" },
				client: { label: "לקוח", color: "success", icon: "lucide:user" },
			};
			const m = meta[value as TRequest["createdBy"]];
			if (!m) return <span className="text-default-400">—</span>;
			return (
				<Chip
					size="sm"
					variant="flat"
					color={m.color}
					startContent={<Icon icon={m.icon} className="h-3 w-3" />}
				>
					{m.label}
				</Chip>
			);
		}

		if (key === "status") {
			const color: Record<TRequest["status"], ChipProps["color"]> = {
				completed: "success",
				rejected: "danger",
				rejected_by_doctor: "danger",
				approved_by_doctor: "secondary",
				draft: "default",
				pending: "primary",
				pending_doctor: "primary",
				change_required: "warning",
				change_required_broker: "warning",
				pending_external_documents: "warning",
			};
			return (
				<Chip color={color[value as TRequest["status"]]} variant="solid" radius="md">
					{t(`common:requestStatus.${value as TRequest["status"]}`)}
				</Chip>
			);
		}

		if (key === "doctor.id") {
			if (!value) {
				return (
					<span className="text-default-400">{t("admin:requests.table.notAssigned")}</span>
				);
			}
			const doctor = doctors.find((d) => d.id === value);
			return doctor
				? `${doctor.firstName} ${doctor.lastName}`
				: `${t("admin:requests.table.doctorId")}: ${value}`;
		}

		if (key === "brokerId") {
			if (!value) {
				return <span className="text-default-400">-</span>;
			}
			const broker = brokers.find((b) => b.id === value);
			return broker ? `${broker.firstName} ${broker.lastName}` : value;
		}

		if ((key as any) === "actions") {
			return <RequestActionsCell onSave={onSave} onView={onView} doctors={doctors} request={item} />;
		}

		return value as any;
	};

	return (
		<Table isStriped aria-label={t("admin:requests.title")}>
			<TableHeader columns={columns}>
				{(column) => (
					<TableColumn
						align={(column.key as any) === "actions" ? "center" : "start"}
						key={column.key}
					>
						{column.label}
					</TableColumn>
				)}
			</TableHeader>
			<TableBody emptyContent={t("admin:requests.emptyState")} items={requests}>
				{(item) => (
					<TableRow key={item.id}>
						{(columnKey: any) => <TableCell>{renderCell(item, columnKey)}</TableCell>}
					</TableRow>
				)}
			</TableBody>
		</Table>
	);
}

function RequestActionsCell({
	request,
	onSave,
	onView,
}: {
	onSave?: () => void;
	onView?: (request: TRequest) => void;
	request: TRequest;
	doctors: TDoctor[];
}) {
	const { t } = useTranslation(["admin", "common"]);

	const appApi = useAppApi();

	const { user } = useStore();

	const viewRequest = (id: string) => {
		appApi.navigate(`/admin/requests/${id}`);
	};

	const confirmDelete = useConfirm({
		title: t("admin:confirmations.deleteRequest.title"),
		message: t("admin:confirmations.deleteRequest.message"),
		confirmText: t("admin:confirmations.deleteRequest.confirmText"),
		cancelText: t("admin:confirmations.deleteRequest.cancelText"),
		onConfirm: async (close) => {
			// Delete the request document - Firebase Storage cleanup will be handled by Cloud Function
			const res = await appApi.api.admin.deleteRequest({ id: request.id });
			if (res.success && onSave) {
				close();
				onSave();
			}
		},
	});

	if (user?.admin) {
		return (
			<div className="inline-flex gap-2">
				<Button
					variant="bordered"
					onPress={() => {
						viewRequest(request.id);
					}}
					color="secondary"
				>
					{t("admin:requests.view")}
				</Button>
				<Button
					color="danger"
					variant="flat"
					onPress={confirmDelete}
					startContent={<Icon icon="lucide:trash-2" />}
				>
					{t("admin:requests.remove")}
				</Button>
			</div>
		);
	}

	if (user?.recommendingDoctor) {
		return (
			<div className="inline-flex gap-2">
				<Button
					variant="bordered"
					onPress={() => {
						viewRequest(request.id);
					}}
					color="secondary"
				>
					{t("admin:requests.view")}
				</Button>
			</div>
		);
	}

	if (user?.broker && request.status !== "completed" && request.status !== "rejected") {
		return (
			<div className="inline-flex gap-2">
				<Button
					variant="bordered"
					color="secondary"
					onPress={() => {
						appApi.navigate(`/broker/requests/${request.id}/edit`);
					}}
					startContent={<Icon icon="lucide:edit" />}
				>
					ערוך
				</Button>
			</div>
		);
	}

	if (onView) {
		return (
			<Button
				variant="bordered"
				color="secondary"
				startContent={<Icon icon="lucide:eye" />}
				onPress={() => onView(request)}
			>
				צפייה
			</Button>
		);
	}

	return null;
}
